/**
 * In-memory session (Constitution XII): the JWT lives in this closure and
 * nowhere else — never Web Storage, never cookies set by JS. After a full page reload the API may
 * re-mint it from the separate httpOnly refresh cookie; page scripts still never persist either
 * credential themselves.
 */
export interface SessionClaims {
  token: string;
  perfil: 'gestor' | 'servidor';
  prefeituraId: string;
  servidorId: string;
  expiresAt: number; // epoch seconds
  session?: SessionMetadata;
  /* Feature 040 (LYN-26 Block 2): who signed in and where. Optional on purpose — a token minted
     before the feature lives up to fifteen minutes, and it must still open a session; the shell
     renders an honest fallback (the e-mail's local part; an empty município line), never a fixture. */
  email?: string | undefined;
  nome?: string | undefined;
  municipio?: string | undefined;
  uf?: string | undefined;
  /* Feature 060 (LYN-8): as secretarias vinculadas — o MESMO claim que a API já usa para montar
     a visibilidade do servidor (Program.cs, `secretaria_ids`). Fonte honesta para "só as pastas
     em que ele tem vínculo" no seletor de recorte (FR-019); derivar das linhas carregadas seria
     a mesma mentira em escala, só que na OFERTA do chip. Ausente para gestor, ou para um token
     anterior a esta feature. */
  secretariaIds?: string[] | undefined;
}

/** Non-secret server metadata; deliberately memory-only like the access token. */
export interface SessionMetadata {
  mode: 'session' | 'persistent';
  expiresAt: string;
  recentAuthenticationValidUntil: string;
}

import { decodeJwtPayload } from './jwt-payload';

export type ExpireReason = 'expired' | 'permission';

interface StoreState {
  claims: SessionClaims | null;
  /** True when the session ended involuntarily (401/exp/403-mutation) — login explains why. */
  expired: boolean;
  expiredReason: ExpireReason | null;
}

type Listener = () => void;
type AccessTokenRefresher = () => Promise<boolean>;

/**
 * Feature 060 (LYN-8): o claim `secretaria_ids` chega de DUAS formas possíveis — o JWT real
 * (`JwtSecurityTokenHandler`) serializa UM valor como string solta e DOIS OU MAIS como array; só
 * aceitar array (como o texto da tarefa lê ao pé da letra) perderia o servidor mais comum do ICP,
 * o vinculado a uma ÚNICA secretaria. Ausente ou de outro tipo → `undefined` (malformado).
 */
function secretariaIdsDoClaim(valor: unknown): string[] | undefined {
  if (typeof valor === 'string') return [valor];
  if (Array.isArray(valor) && valor.every((item): item is string => typeof item === 'string')) {
    return valor;
  }
  return undefined;
}

function decodeJwtClaims(token: string): SessionClaims | null {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;
  try {
    const perfil = decoded['perfil'];
    const prefeituraId = decoded['prefeitura_id'];
    const servidorId = decoded['servidor_id'];
    const exp = decoded['exp'];
    if (
      (perfil !== 'gestor' && perfil !== 'servidor') ||
      typeof prefeituraId !== 'string' ||
      typeof servidorId !== 'string' ||
      typeof exp !== 'number'
    ) {
      return null;
    }
    const opcional = (chave: string): string | undefined => {
      const valor = decoded[chave];
      return typeof valor === 'string' ? valor : undefined;
    };
    return {
      token,
      perfil,
      prefeituraId,
      servidorId,
      expiresAt: exp,
      email: opcional('email'),
      nome: opcional('nome'),
      municipio: opcional('municipio'),
      uf: opcional('uf'),
      secretariaIds: secretariaIdsDoClaim(decoded['secretaria_ids']),
    };
  } catch {
    return null;
  }
}

function isSessionMetadata(value: unknown): value is SessionMetadata {
  if (!value || typeof value !== 'object') return false;
  const metadata = value as Record<string, unknown>;
  return (metadata.mode === 'session' || metadata.mode === 'persistent')
    && typeof metadata.expiresAt === 'string'
    && typeof metadata.recentAuthenticationValidUntil === 'string';
}

function createSessionStore() {
  let state: StoreState = { claims: null, expired: false, expiredReason: null };
  const listeners = new Set<Listener>();
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let accessTokenRefresher: AccessTokenRefresher | null = null;

  function emit() {
    for (const listener of listeners) listener();
  }

  function setState(next: StoreState) {
    state = next;
    emit();
  }

  function stopTimer() {
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }

  return {
    getState: () => state,
    getToken: () => state.claims?.token ?? null,
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** Installs the silent-refresh trigger used when the short-lived JWT reaches its expiry. */
    setAccessTokenRefresher(refresher: AccessTokenRefresher) {
      const previous = accessTokenRefresher;
      accessTokenRefresher = refresher;
      return () => {
        if (accessTokenRefresher === refresher) accessTokenRefresher = previous;
      };
    },
    /** Returns false when the token is malformed or already expired. */
    signIn(token: string, session?: unknown): boolean {
      const claims = decodeJwtClaims(token);
      if (!claims || claims.expiresAt * 1000 <= Date.now()) return false;
      if (session !== undefined && !isSessionMetadata(session)) return false;
      const claimsWithSession = session === undefined ? claims : { ...claims, session };
      stopTimer();
      expiryTimer = setTimeout(
        () => {
          expiryTimer = undefined;
          if (state.claims?.token !== claimsWithSession.token) return;

          const refresher = accessTokenRefresher;
          if (!refresher) {
            this.expire();
            return;
          }

          void refresher()
            .then((refreshed) => {
              if (!refreshed && state.claims?.token === claimsWithSession.token) this.expire();
            })
            .catch(() => {
              if (state.claims?.token === claimsWithSession.token) this.expire();
            });
        },
        Math.min(claims.expiresAt * 1000 - Date.now(), 2 ** 31 - 1),
      );
      setState({ claims: claimsWithSession, expired: false, expiredReason: null });
      return true;
    },
    /** Involuntary end: 401/exp ('expired') or 403 on a mutation ('permission'). */
    expire(reason: ExpireReason = 'expired') {
      stopTimer();
      if (state.claims || !state.expired) {
        setState({ claims: null, expired: true, expiredReason: reason });
      }
    },
    /** Voluntary end (logout) or test reset. */
    clear() {
      stopTimer();
      setState({ claims: null, expired: false, expiredReason: null });
    },
  };
}

export const sessionStore = createSessionStore();
